import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, X, CheckCircle2 } from 'lucide-react';
import api from '@/store/api';

/**
 * Lightweight password-reset modal. Calls the existing backend endpoint
 * `/api/auth/password-reset/request` which always returns 200 (anti-
 * enumeration) so we show a generic success message regardless of whether
 * the email is registered. This unblocks users who locked themselves out.
 */
export default function ForgotPasswordModal({ isOpen, onClose, initialEmail = '' }) {
  const [email, setEmail] = useState(initialEmail);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (isOpen) {
      setEmail(initialEmail);
      setSent(false);
      setError('');
    }
  }, [isOpen, initialEmail]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = (email || '').trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Ingresa un correo válido');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api('/api/auth/password-reset/request', {
        method: 'POST',
        body: JSON.stringify({ email: trimmed }),
      });
      setSent(true);
    } catch (e) {
      // Backend always returns 200 — any error here is network/transport.
      setError('No pudimos enviar el correo. Intenta nuevamente en unos segundos.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 px-4"
        onClick={onClose}
        data-testid="forgot-password-overlay"
      >
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ type: 'spring', damping: 24, stiffness: 280 }}
          className="bg-white rounded-3xl md:rounded-2xl w-full max-w-md p-6 md:p-7 shadow-xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="forgot-password-modal"
        >
          <div className="flex items-start justify-between mb-4">
            <h2 className="text-xl font-bold text-black tracking-tight">
              Restablecer contraseña
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 -mr-1 -mt-1 p-1"
              data-testid="forgot-password-close"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {sent ? (
            <div className="text-center py-4" data-testid="forgot-password-success">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-green-50 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-sm text-gray-700 font-medium mb-1">
                Si el correo está registrado, recibirás un enlace para restablecer tu contraseña.
              </p>
              <p className="text-xs text-gray-500 mb-5">
                Revisa también tu carpeta de spam.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="w-full bg-[#fbb040] text-black font-bold rounded-full h-12 hover:bg-[#ffa000] transition-colors"
                data-testid="forgot-password-done"
              >
                Entendido
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="forgot-password-form">
              <p className="text-sm text-gray-500 font-light mb-4 leading-snug">
                Ingresa el correo con el que te registraste y te enviaremos un enlace para crear una nueva contraseña.
              </p>

              <div className={`flex items-center gap-3 bg-gray-50 rounded-xl px-4 h-14 border-b-2 ${error ? 'border-red-400' : 'border-transparent focus-within:border-[#fbb040]'} transition-all`}>
                <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <input
                  type="email"
                  placeholder="Correo electrónico"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  className="flex-1 bg-transparent outline-none text-base font-medium placeholder:text-gray-400"
                  autoFocus
                  data-testid="forgot-password-email-input"
                />
              </div>
              {error && (
                <p className="text-red-500 text-xs mt-1 ml-1" data-testid="forgot-password-error">{error}</p>
              )}

              <motion.button
                type="submit"
                whileTap={{ scale: 0.97 }}
                disabled={submitting}
                className={`w-full mt-5 bg-[#fbb040] text-black font-bold rounded-full h-13 py-3.5 hover:bg-[#ffa000] transition-colors ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                data-testid="forgot-password-submit"
              >
                {submitting ? 'Enviando...' : 'Enviar enlace'}
              </motion.button>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
